import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="membership-plan-form"
export default class extends Controller {
  static targets = [
    "featuresContainer",
    "featuresInput",
    "newFeatureInput",
    "previewFeatures",
    "previewName",
    "previewDescription",
    "previewPrice",
    "previewCurrency",
    "previewCycle",
    "previewPopularBadge",
    "previewClassLimit"
  ]

  static values = {
    features: { type: Array, default: [] }
  }

  connect() {
    console.log("Membership plan form controller connected")
    this.renderFeatures()
    this.updatePreview()
  }

  addFeature() {
    const featureText = this.newFeatureInputTarget.value.trim()

    if (featureText) {
      this.featuresValue = [...this.featuresValue, featureText]
      this.renderFeatures()
      this.updateFeaturesInput()
      this.updatePreviewFeatures()
      this.newFeatureInputTarget.value = ''
    }
  }

  removeFeature(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.featuresValue = this.featuresValue.filter((_, i) => i !== index)
    this.renderFeatures()
    this.updateFeaturesInput()
    this.updatePreviewFeatures()
  }

  renderFeatures() {
    this.featuresContainerTarget.innerHTML = this.featuresValue.map((feature, index) => `
      <div class="flex items-center space-x-3 p-3 bg-gray-50 rounded-md group">
        <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <span class="flex-1 text-sm text-gray-700">${feature}</span>
        <button type="button"
                data-index="${index}"
                data-action="click->membership-plan-form#removeFeature"
                class="text-red-600 hover:text-red-800 opacity-0 group-hover:opacity-100 transition-opacity">
          <svg class="w-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `).join('')
  }

  updateFeaturesInput() {
    if (this.hasFeaturesInputTarget) {
      this.featuresInputTarget.value = JSON.stringify(this.featuresValue)
    }
  }

  updatePreviewFeatures() {
    if (!this.hasPreviewFeaturesTarget) return

    const featuresList = this.featuresValue.map(feature => `
      <div class="flex items-start space-x-2">
        <svg class="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <span class="text-sm text-gray-700">${feature}</span>
      </div>
    `).join('')

    this.previewFeaturesTarget.innerHTML = `
      <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">What's Included:</p>
      ${featuresList || '<p class="text-xs text-gray-400 italic">No features added yet</p>'}
    `
  }

  updatePreview() {
    const form = this.element.querySelector('form')
    if (!form) return

    // Update name
    const name = form.querySelector('[name="membership_plan[name]"]')?.value || 'Plan Name'
    if (this.hasPreviewNameTarget) {
      this.previewNameTarget.textContent = name
    }

    // Update description
    const description = form.querySelector('[name="membership_plan[description]"]')?.value || 'Plan description will appear here'
    if (this.hasPreviewDescriptionTarget) {
      this.previewDescriptionTarget.textContent = description
    }

    // Update price
    const price = form.querySelector('[name="membership_plan[price]"]')?.value || '0'
    if (this.hasPreviewPriceTarget) {
      this.previewPriceTarget.textContent = '$' + parseFloat(price).toFixed(2)
    }

    // Update currency
    const currency = form.querySelector('[name="membership_plan[currency]"]')?.value || 'MXN'
    if (this.hasPreviewCurrencyTarget) {
      this.previewCurrencyTarget.textContent = currency
    }

    // Update billing cycle
    const cycle = form.querySelector('[name="membership_plan[billing_cycle]"]')?.value || 'monthly'
    const cycleText = {
      'monthly': '/month',
      'quarterly': '/3 months',
      'yearly': '/year'
    }[cycle] || '/month'
    if (this.hasPreviewCycleTarget) {
      this.previewCycleTarget.textContent = cycleText
    }

    // Update popular badge
    const popular = form.querySelector('[name="membership_plan[popular]"]')?.checked
    if (this.hasPreviewPopularBadgeTarget) {
      this.previewPopularBadgeTarget.classList.toggle('hidden', !popular)
    }

    // Update class limit
    const unlimited = form.querySelector('[name="membership_plan[unlimited_classes]"]')?.checked
    const credits = form.querySelector('[name="membership_plan[credits_per_cycle]"]')?.value

    let classLimitText = 'No class access'
    if (unlimited) {
      classLimitText = 'Unlimited classes'
    } else if (credits) {
      const period = {
        'monthly': 'month',
        'quarterly': 'quarter',
        'yearly': 'year'
      }[cycle] || 'period'
      classLimitText = `${credits} classes per ${period}`
    }

    if (this.hasPreviewClassLimitTarget) {
      this.previewClassLimitTarget.innerHTML = `<p class="text-sm font-medium text-indigo-900">${classLimitText}</p>`
    }
  }
}
